import { useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, ChatCircle, X } from "@phosphor-icons/react";

function OutreachModal({ kind, customer, onClose, onSent }) {
  const [subject, setSubject] = useState(
    kind === "email" ? `Following up on your visit, ${customer.name.split(" ")[0]}` : ""
  );
  const [body, setBody] = useState(
    kind === "email"
      ? `Hi ${customer.name.split(" ")[0]},\n\nThanks for stopping by. Let me know if you have any questions about the vehicles we discussed.\n\nBest,\nMotorLedger Team`
      : `Hi ${customer.name.split(" ")[0]}, following up — let me know if you have any questions.`
  );
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    // MOCKED: no real send. Simulate latency.
    await new Promise((r) => setTimeout(r, 600));
    toast.success(`${kind === "email" ? "Email" : "SMS"} sent (MOCKED — no provider configured)`);
    setSending(false);
    onSent({ type: kind, content: kind === "email" ? `[Email] ${subject}\n${body}` : `[SMS] ${body}` });
    onClose();
  };

  const Icon = kind === "email" ? EnvelopeSimple : ChatCircle;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-ink/50" onClick={onClose} />
      <div className="relative bg-canvas-card border border-ink w-full max-w-lg" data-testid={`outreach-${kind}-modal`}>
        <div className="px-6 py-5 border-b border-canvas-line flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon size={16} weight="bold" />
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted">
                Send {kind}
              </div>
              <h2 className="font-display text-lg font-bold text-ink mt-0.5">{customer.name}</h2>
            </div>
          </div>
          <button onClick={onClose} className="p-2 border border-canvas-line hover:border-ink">
            <X size={14} weight="bold" />
          </button>
        </div>
        <form onSubmit={send} className="p-6 space-y-3">
          <div className="px-3 py-2 border border-canvas-line bg-canvas-panel font-mono text-[11px] text-ink-muted">
            To: {kind === "email" ? customer.email : customer.phone}
          </div>
          {kind === "email" && (
            <input
              data-testid={`${kind}-subject`}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              required
            />
          )}
          <textarea
            data-testid={`${kind}-body`}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={kind === "email" ? 8 : 4}
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            required
          />
          <div className="px-3 py-2 border border-[#FFD700] bg-[#FFFBE6] font-mono text-[10px] uppercase tracking-wider text-ink">
            ⚠ MOCKED — this will log to activity but not actually send.
          </div>
          <div className="flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-canvas-line text-sm hover:border-ink"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending}
              data-testid={`${kind}-send`}
              className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60"
            >
              {sending ? "Sending…" : `Send ${kind}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function OutreachButtons({ customer, onLogged }) {
  const [open, setOpen] = useState(null); // 'email' | 'sms' | null
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen("email")}
        data-testid="outreach-email-button"
        className="px-3 py-2 border border-canvas-line text-sm hover:border-ink flex items-center gap-1.5"
      >
        <EnvelopeSimple size={13} weight="bold" />
        Email
      </button>
      <button
        type="button"
        onClick={() => setOpen("sms")}
        data-testid="outreach-sms-button"
        className="px-3 py-2 border border-canvas-line text-sm hover:border-ink flex items-center gap-1.5"
      >
        <ChatCircle size={13} weight="bold" />
        SMS
      </button>
      {open && (
        <OutreachModal
          kind={open}
          customer={customer}
          onClose={() => setOpen(null)}
          onSent={onLogged}
        />
      )}
    </>
  );
}
